import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import PayoutEstimator from '../../components/PayoutEstimator';
import { 
  DollarSign, CheckCircle2, Clock, XCircle, Calendar, CreditCard, AlertCircle,
  ArrowRight, Download, FileText, Receipt, X, Printer, Calculator, Info,
  ArrowDown, Minus, Equal, Pencil, Lock, ShieldCheck, Loader2, Wallet
} from 'lucide-react';
import ShippingPoolWalletPanel from '../../components/vendor/ShippingPoolWalletPanel';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useToast } from '../../hooks/use-toast';

const VendorPayouts = () => {
  const navigate = useNavigate();
  const { user, apiCall } = useAuth();
  const { formatPrice } = useCurrency();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [payouts, setPayouts] = useState([]);
  const [stats, setStats] = useState({
    pending_payouts: 0,
    processing_payouts: 0,
    completed_payouts: 0,
    total_paid: 0
  });

  const [payoutData, setPayoutData] = useState({
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    payout_frequency: 'monthly',
    legal_business_name: '',
    gst_number: '',
    pan_number: '',
    enrollment_id: '',
    iec_code: '',
    billing_address: '',
    commission_rate: '15',
    payout_threshold: '1000',
    show_total_commission: true,
    show_balance_due: true
  });
  
  const [upcomingPayouts, setUpcomingPayouts] = useState([]);
  const [upcomingData, setUpcomingData] = useState({
    next_payout_date: null,
    expected_amount: 0,
    total_orders: 0
  });
  
  // Settlement Slip Modal State — now shows full 10-line SSOT breakdown
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [showSettlementModal, setShowSettlementModal] = useState(false);

  // Per-order payout history from vendor_payouts (SSOT)
  const [payoutHistory, setPayoutHistory] = useState([]);

  // Edit Bank Details State
  const [isEditingBank, setIsEditingBank] = useState(false);
  const [editBankData, setEditBankData] = useState({ bank_name: '', account_number: '', ifsc_code: '', payout_frequency: 'monthly' });
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  useEffect(() => {
    fetchPayoutData();
    fetchPayouts();
    fetchUpcomingPayouts();
    fetchPayoutHistory();
  }, []);

  const fetchPayoutData = async () => {
    try {
      const unifiedProfile = await apiCall(`/api/vendors/complete-profile/me`);
      const profileData = await apiCall(`/api/users/me`);
      
      const autoFilledData = {
        bank_name: unifiedProfile.bank?.bank_name || '',
        account_number: unifiedProfile.bank?.account_number || '',
        ifsc_code: unifiedProfile.bank?.ifsc_code || '',
        legal_business_name: unifiedProfile.business?.legal_name || unifiedProfile.business?.trade_name || '',
        gst_number: unifiedProfile.business?.gstin || '',
        pan_number: unifiedProfile.business?.pan_number || '',
        enrollment_id: unifiedProfile.business?.enrollment_id || '',
        iec_code: unifiedProfile.business?.iec_code || '',
        billing_address: unifiedProfile.addresses?.billing?.full_address || unifiedProfile.addresses?.business?.full_address || '',
        payout_frequency: profileData.vendor_profile?.payout_details?.payout_frequency || 'monthly',
        payout_threshold: profileData.vendor_profile?.payout_details?.payout_threshold || '1000',
        commission_rate: profileData.vendor_profile?.payout_details?.commission_rate || '15',
        show_total_commission: profileData.vendor_profile?.payout_details?.show_total_commission !== false,
        show_balance_due: profileData.vendor_profile?.payout_details?.show_balance_due !== false
      };
      
      setPayoutData(prev => ({ ...prev, ...autoFilledData }));
    } catch (error) {
      console.error('Failed to fetch payout data:', error);
    }
  };

  const fetchPayouts = async () => {
    try {
      const payoutsData = await apiCall('/api/payouts/');
      setPayouts(payoutsData || []);
      
      const statsData = await apiCall('/api/payouts/stats');
      setStats({
        pending_payouts: statsData.pending_payouts || 0,
        processing_payouts: statsData.processing_payouts || 0,
        completed_payouts: statsData.completed_payouts || 0,
        total_paid: statsData.total_paid_out || 0
      });
    } catch (error) {
      console.error('Failed to load payouts:', error);
    }
  };

  const fetchUpcomingPayouts = async () => {
    try {
      const data = await apiCall('/api/payouts/upcoming');
      setUpcomingPayouts(data.upcoming_payouts || []);
      setUpcomingData({
        next_payout_date: data.next_payout_date,
        expected_amount: data.expected_amount || 0,
        total_orders: data.total_orders || 0
      });
    } catch (error) {
      console.error('Failed to load upcoming payouts:', error);
    }
  };

  const fetchPayoutHistory = async () => {
    try {
      const rows = await apiCall('/api/payouts/vendor-history?limit=200');
      setPayoutHistory(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error('Failed to load payout history:', error);
    }
  };

  const handleSavePayoutSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiCall(`/api/users/me`, {
        method: 'PUT',
        body: JSON.stringify({
          vendor_profile: {
            payout_details: payoutData
          }
        })
      });
      toast({ title: 'Success', description: 'Payout settings updated successfully' });
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to update settings',
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  // ---- Edit Bank Details Handlers ----
  const handleStartEdit = () => {
    setEditBankData({
      bank_name: payoutData.bank_name,
      account_number: payoutData.account_number,
      ifsc_code: payoutData.ifsc_code,
      payout_frequency: payoutData.payout_frequency
    });
    setIsEditingBank(true);
  };

  const handleCancelEdit = () => {
    setIsEditingBank(false);
    setEditBankData({ bank_name: '', account_number: '', ifsc_code: '', payout_frequency: 'monthly' });
  };

  const handleRequestOtp = async () => {
    const mobile = user?.mobile;
    if (!mobile) {
      toast({ title: 'Error', description: 'No mobile number found on your account. Contact admin.', variant: 'destructive' });
      return;
    }
    // Basic validation on edited fields
    if (!editBankData.bank_name || !editBankData.account_number || !editBankData.ifsc_code) {
      toast({ title: 'Missing Fields', description: 'Please fill all bank detail fields.', variant: 'destructive' });
      return;
    }
    setOtpSending(true);
    try {
      await apiCall('/api/auth/send-otp', {
        method: 'POST',
        body: JSON.stringify({ mobile })
      });
      setOtpSent(true);
      setShowOtpModal(true);
      toast({ title: 'OTP Sent', description: `A verification code has been sent to your registered mobile ending ****${mobile.slice(-4)}.` });
    } catch (err) {
      toast({ title: 'OTP Failed', description: err.message || 'Could not send OTP. Try again.', variant: 'destructive' });
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyAndSave = async () => {
    if (!otpValue || otpValue.length < 4) {
      toast({ title: 'Enter OTP', description: 'Please enter the verification code.', variant: 'destructive' });
      return;
    }
    setOtpVerifying(true);
    try {
      const result = await apiCall('/api/payouts/bank-details', {
        method: 'PUT',
        body: JSON.stringify({
          otp: otpValue,
          mobile: user?.mobile,
          bank_name: editBankData.bank_name,
          account_number: editBankData.account_number,
          ifsc_code: editBankData.ifsc_code,
          payout_frequency: editBankData.payout_frequency
        })
      });
      toast({ title: 'Updated', description: result.message || 'Bank details updated successfully.' });
      // Refresh data and exit edit mode
      setPayoutData(prev => ({
        ...prev,
        bank_name: editBankData.bank_name,
        account_number: editBankData.account_number,
        ifsc_code: editBankData.ifsc_code,
        payout_frequency: editBankData.payout_frequency
      }));
      setIsEditingBank(false);
      setShowOtpModal(false);
      setOtpValue('');
      setOtpSent(false);
    } catch (err) {
      toast({ title: 'Verification Failed', description: err.message || 'Could not verify OTP.', variant: 'destructive' });
    } finally {
      setOtpVerifying(false);
    }
  };



  const getStatusBadge = (status) => {
    // iter-308a — was a 4-entry map (pending/processing/completed/failed).
    // The backend's real per-order statuses (held/released/paid/reversed/
    // cancelled/skipped/computed — see `/vendor-history`) fell through to
    // `statusConfig.pending` for anything not one of those 4 exact
    // strings, which is EXACTLY how a genuinely completed/released
    // payout could render as a yellow "Pending" badge.
    const statusConfig = {
      pending:     { label: 'Pending',       class: 'bg-yellow-100 text-yellow-800', icon: Clock },
      held:        { label: 'Held (Escrow)', class: 'bg-blue-100 text-blue-800',     icon: Loader2 },
      processing:  { label: 'Processing',    class: 'bg-blue-100 text-blue-800',     icon: DollarSign },
      released:    { label: 'Completed',     class: 'bg-green-100 text-green-800',   icon: CheckCircle2 },
      paid:        { label: 'Completed',     class: 'bg-green-100 text-green-800',   icon: CheckCircle2 },
      completed:   { label: 'Completed',     class: 'bg-green-100 text-green-800',   icon: CheckCircle2 },
      failed:      { label: 'Failed',        class: 'bg-red-100 text-red-800',       icon: XCircle },
      reversed:    { label: 'Reversed',      class: 'bg-orange-100 text-orange-800', icon: AlertCircle },
      cancelled:   { label: 'Cancelled',     class: 'bg-gray-200 text-gray-700',      icon: XCircle },
      skipped:     { label: 'Skipped',       class: 'bg-gray-200 text-gray-700',      icon: AlertCircle },
      computed:    { label: 'Estimated',     class: 'bg-purple-100 text-purple-800', icon: Clock },
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge className={config.class}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  // iter-308a — the "Upcoming" tab hard-coded every row's badge to
  // "Pending" regardless of real state. The backend already computes a
  // real `payout_category` per row ("ready" = past the escrow window and
  // due to be transferred; "upcoming" = order hasn't reached that stage
  // yet) — surface that instead of a static label.
  const getUpcomingBadge = (status) => {
    if (status === 'ready') {
      return (
        <Badge className="bg-emerald-100 text-emerald-800" data-testid="upcoming-status-ready">
          <CheckCircle2 className="h-3 w-3 mr-1" /> Ready for Payout
        </Badge>
      );
    }
    return (
      <Badge className="bg-yellow-100 text-yellow-800" data-testid="upcoming-status-pending">
        <Clock className="h-3 w-3 mr-1" /> Upcoming
      </Badge>
    );
  };

  const handleViewSettlement = (payout) => {
    setSelectedPayout(payout);
    setShowSettlementModal(true);
  };

  // iter-316 Phase 3 — Payout Receipt view/download (real implementation,
  // replaces the previous placeholder toast-only stub). Ownership is
  // enforced server-side; vendor sees only their own payout receipts.
  const RELEASED_STATUSES = ['released', 'paid', 'completed'];

  const openPayoutReceipt = async (payout, download = false, version = 'corrected') => {
    const shipmentId = payout.id || payout.shipment_id;
    if (!shipmentId) {
      toast({ title: 'Receipt unavailable', description: 'No shipment record for this payout.', variant: 'destructive' });
      return;
    }
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    try {
      const path = download ? 'pdf' : 'html';
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payouts/${shipmentId}/receipt/${path}?version=${version}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      if (download) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `payout-receipt-${payout.order_number || shipmentId.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast({ title: 'Receipt unavailable', description: e?.message || String(e), variant: 'destructive' });
    }
  };

  const handleDownloadTaxInvoice = async (payout) => {
    try {
      // Try to get from Zoho if connected
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/zoho/invoices/order/${payout.order_id}/pdf`,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else {
        toast({
          title: 'Tax Invoice Not Available',
          description: 'Connect Zoho Invoice to generate tax invoices.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Tax Invoice Not Available',
        description: 'Please ensure Zoho Invoice is connected.',
        variant: 'destructive'
      });
    }
  };

  // ─── Full SSOT Payout Breakdown Modal ────────────────────────────────────────
  // Mirrors the Admin "TransactionBreakdownDrawer" (AdminPayouts.jsx).
  // Receives either an upcoming payout row (has .breakdown) or a
  // vendor-history row (also has .breakdown).
  const PayoutBreakdownModal = ({ payout, onClose }) => {
    if (!payout) return null;
    const b = payout.breakdown || {};
    const fmt = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const isSelfShipping = (b.shipping_mode || payout.shipping_mode) === 'self';

    const commRateRaw = b.commission_rate || b.commission_rate_used || payout.commission_rate_used;
    const commRatePct = commRateRaw != null
      ? (parseFloat(commRateRaw) < 1.5 ? parseFloat(commRateRaw) * 100 : parseFloat(commRateRaw))
      : null;
    const commPctDisplay = commRatePct != null ? commRatePct.toFixed(1).replace(/\.0$/, '') : '—';

    const commSource = b.commission_rate_source || b.commission_rate_source_v2;
    const commSourceLabel = (() => {
      if (!commSource) return 'Subscription plan';
      if (commSource.startsWith('override')) return 'Custom Override';
      if (commSource === 'MISSING_PLAN') return 'Missing Plan';
      if (commSource === 'subscription_history') return 'Subscription (plan active on order date)';
      return commSource.replace(/_/g, ' ');
    })();

    const pgFee      = Number(b.pg_fee) || Number(b.gateway_fee) || 0;
    const pgGst      = Number(b.pg_gst) || Number(b.gateway_gst) || 0;
    const pgTotal    = pgFee + pgGst;
    const sellerPool = Number(b.seller_pool) || Number(b.shipping_subsidy) || 0;

    const grossDisplay = b.gross_total || b.gross
      || payout.gross_total
      || (b.commission && b.commission_rate ? Number(b.commission) / Number(b.commission_rate) : 0);

    // iter-319 — FIX: this used to back-derive tds%/tcs% as
    // `amount ÷ approximated_tax_base` (gross − shipping − shipping GST),
    // but that approximation does NOT match the engine's real tax base
    // (product_price + shipping_base + shipping_gst, excluding surcharge —
    // see payout_engine.py PayoutBreakdown docstring). That mismatch made
    // the displayed % drift from the true rate, and for legacy records it
    // silently showed today's default rate next to an amount that may
    // have been computed under an older rate. Read the stored
    // `tds_rate`/`tcs_rate` SSOT echo fields directly instead — exactly
    // like `commission_rate` above — and show "rate not recorded" rather
    // than guessing when a legacy record doesn't carry them.
    const formatStoredPct = (rate) => {
      if (rate == null) return null;
      const v = parseFloat(rate);
      if (Number.isNaN(v)) return null;
      const pct = v < 1.5 ? v * 100 : v;
      return pct.toFixed(2).replace(/\.?0+$/, '');
    };
    const tdsPctLabel = formatStoredPct(b.tds_rate);
    const tcsPctLabel = formatStoredPct(b.tcs_rate);
    const pgFeePct = grossDisplay > 0 ? (pgFee / grossDisplay * 100).toFixed(2) : '2.00';

    const netPayout = b.net_payout || payout.net_payout || payout.net_amount || 0;
    const correction = payout.correction_info;
    const hasCorrection = !!payout.has_correction && !!correction;

    const lines = [
      {
        label: 'Total Amount Received (Gross)',
        value: grossDisplay, sign: '+', tone: 'text-gray-900',
        sub: isSelfShipping
          ? 'Product + Customer-paid shipping + Shipping GST'
          : 'Product price + Platform shipping + 18% Shipping GST',
      },
      {
        label: `Platform Commission (${commPctDisplay}%)`,
        value: b.commission, sign: '−', tone: 'text-red-600',
        sub: commSourceLabel,
      },
      {
        label: 'GST on Commission (18%)',
        value: b.commission_gst, sign: '−', tone: 'text-red-600',
        sub: 'CGST + SGST on the commission amount',
      },
      {
        label: 'Shipping Pool Contribution',
        value: sellerPool, sign: '−', tone: 'text-red-600',
        sub: '5% × Product price (capped)',
        hide: isSelfShipping || sellerPool === 0,
      },
      {
        // iter-376 — was previously folded silently into the net figure
        // with no line of its own for Aniyor-managed orders.
        label: 'Shipping Amount (retained by Aniyor)',
        value: Number(b.shipping_base) || 0, sign: '−', tone: 'text-red-600',
        sub: 'Platform-managed shipping is invoiced/settled separately — not paid out to you',
        hide: isSelfShipping || (Number(b.shipping_base) || 0) === 0,
      },
      {
        label: 'Shipping GST (18% on Shipping)',
        value: b.shipping_gst, sign: isSelfShipping ? '+' : '−',
        tone: isSelfShipping ? 'text-emerald-700' : 'text-red-600',
        sub: isSelfShipping ? 'Passed through to you' : 'Retained by platform',
        hide: (Number(b.shipping_base) || Number(b.shipping) || 0) === 0,
      },
      {
        label: `TCS — GST u/s 52${tcsPctLabel ? ` (${tcsPctLabel}%)` : ' (rate not recorded)'}`,
        value: b.tcs, sign: '−', tone: 'text-red-600',
        sub: 'Tax Collected at Source on gross (claimable as ITC in GST returns)',
      },
      {
        label: `TDS — Income Tax u/s 194-O${tdsPctLabel ? ` (${tdsPctLabel}%)` : ' (rate not recorded)'}`,
        value: b.tds, sign: '−', tone: 'text-red-600',
        sub: 'Deductible against your income tax liability (Form 26AS)',
      },
      {
        label: `Payment Gateway Fee (${pgFeePct}% + 18% GST)`,
        value: pgTotal, sign: '−', tone: 'text-red-600',
        sub: `Razorpay charge ${fmt(pgFee)} + GST ${fmt(pgGst)}`,
      },
      {
        label: 'Razorpay Payout Fee',
        value: b.payout_fee, sign: '−', tone: 'text-red-600',
        sub: 'Per-transfer fee (Razorpay Routes)',
        hide: !b.payout_fee || Number(b.payout_fee) === 0,
      },
    ];

    const orderRef = payout.order_number || payout.order_id;

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
           data-testid="payout-breakdown-modal">
        <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
          {/* Header */}
          <div className="bg-gray-900 text-white px-6 py-4 rounded-t-xl flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-bold text-base">Payout Breakdown</h2>
                {hasCorrection && (
                  <span
                    className="text-[10px] font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded-full px-2 py-0.5"
                    data-testid="modal-correction-badge"
                  >
                    Legacy payout – corrected receipt available
                  </span>
                )}
              </div>
              <p className="text-gray-400 text-xs mt-0.5">Order #{orderRef} — Financial SSOT</p>
            </div>
            <div className="flex items-center gap-3">
              {(RELEASED_STATUSES.includes((payout.status || '').toLowerCase()) || hasCorrection) && (
                <button
                  onClick={() => openPayoutReceipt(payout, true)}
                  className="text-gray-300 hover:text-white transition-colors flex items-center gap-1 text-xs font-medium"
                  data-testid="modal-download-receipt-btn"
                >
                  <Download className="h-4 w-4" /> Receipt
                </button>
              )}
              <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="p-5 space-y-4">
            {/* Status + Date */}
            <div className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-4 py-3">
              <span className="text-gray-500">
                {payout.created_at ? new Date(payout.created_at).toLocaleDateString('en-IN', {
                  day: 'numeric', month: 'short', year: 'numeric'
                }) : ''}
              </span>
              {getStatusBadge(payout.status || payout.order_status || 'computed')}
            </div>

            {/* 10-line breakdown */}
            <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden"
                 data-testid="breakdown-lines">
              {lines.filter(l => !l.hide).map((l, i) => (
                <div key={i} className="flex items-start justify-between px-4 py-3 hover:bg-gray-50">
                  <div className="flex-1 min-w-0 mr-4">
                    <p className="text-sm font-medium text-gray-800">{l.label}</p>
                    {l.sub && <p className="text-[11px] text-gray-400 mt-0.5">{l.sub}</p>}
                  </div>
                  <span className={`text-sm font-mono font-semibold whitespace-nowrap ${l.tone}`}>
                    {l.sign === '+' ? '+ ' : '− '}{fmt(l.value)}
                  </span>
                </div>
              ))}
            </div>

            {/* Net payout */}
            <div className="flex items-center justify-between bg-gray-900 text-white rounded-xl px-5 py-4">
              <div>
                <p className="font-bold text-base">Net Payout</p>
                <p className="text-gray-400 text-xs">Credited to your bank account</p>
              </div>
              <span className="font-bold text-2xl" data-testid="net-payout-amount">
                {fmt(netPayout)}
              </span>
            </div>

            {/* iter-319 — Correction Notes (only when a verified financial
                correction exists for this payout) */}
            {hasCorrection && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 space-y-2"
                   data-testid="modal-correction-notes">
                <p className="text-xs font-bold text-amber-800 uppercase tracking-wide">Correction Notes</p>
                <p className="text-xs text-amber-800 leading-relaxed">
                  This breakdown supersedes a legacy calculation issue. The amounts above are the
                  <b> corrected </b>figures — not what was originally paid.
                </p>
                <div className="text-xs text-amber-800 space-y-0.5">
                  <p>Original Net Payout: <b>{fmt(correction.original_net_payout)}</b></p>
                  <p>Corrected Net Payout: <b>{fmt(correction.corrected_net_payout)}</b></p>
                  <p>
                    Difference:{' '}
                    <b>{correction.difference >= 0 ? '+' : '−'}{fmt(Math.abs(correction.difference))}</b>
                  </p>
                  <p>
                    Adjustment status:{' '}
                    <b className="capitalize">{(correction.status || 'pending').replace(/_/g, ' ')}</b>
                  </p>
                </div>
                <button
                  onClick={() => openPayoutReceipt(payout, false, 'original')}
                  className="text-[11px] font-medium text-amber-700 underline hover:text-amber-900"
                  data-testid="modal-view-legacy-receipt-link"
                >
                  View original legacy receipt
                </button>
              </div>
            )}

            {/* Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-start gap-2">
              <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
              <p className="text-xs text-blue-700">
                TDS certificates are available via Form 26AS. TCS (GST) is claimable as Input Tax Credit in your GST returns.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Payout Logic Card Component (for Formula Tab)
  const PayoutFormulaContent = () => {
    // Example uses 15% commission for illustration
    const illustrativeCommissionRate = 15;
    const sampleGrossSales = 10000;
    const sampleShippingCollected = 150; // Shipping fee collected from customer
    const sampleShippingGST = sampleShippingCollected * 0.18; // 18% GST on shipping
    const sampleTotalGross = sampleGrossSales + sampleShippingCollected + sampleShippingGST; // Total customer pays
    const sampleShippingCost = sampleShippingCollected + sampleShippingGST; // Actual shipping cost = shipping + GST
    const sampleNetTaxable = sampleGrossSales; // Net taxable for TCS (after removing shipping)
    const sampleCommission = sampleGrossSales * (illustrativeCommissionRate / 100);
    const sampleCommissionGST = sampleCommission * 0.18; // 18% GST on Commission
    const sampleTDS = sampleNetTaxable * 0.001; // 0.1% TDS u/s 194-O (on net taxable)
    const sampleTCS = sampleNetTaxable * 0.01; // 1% TCS (GST) on net taxable
    const sampleGateway = sampleTotalGross * 0.02; // 2% Payment Gateway on total collected
    const sampleGatewayGST = sampleGateway * 0.18; // 18% GST on gateway fees
    const sampleTotalDeductions = sampleShippingCost + sampleCommission + sampleCommissionGST + sampleTDS + sampleTCS + sampleGateway + sampleGatewayGST;
    const sampleNetPayout = sampleTotalGross - sampleTotalDeductions;
    
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Payout Formula</h2>
          <p className="text-gray-600 mt-1">Understand how your earnings are calculated (matches Admin view)</p>
        </div>
        
        <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" />
              How Your Payout is Calculated
            </CardTitle>
            <CardDescription>
              Understanding how Gross Sales becomes Net Payout
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Formula */}
            <div className="bg-white border rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-3">The Formula</p>
              <div className="flex items-center gap-2 flex-wrap text-sm">
                <Badge className="bg-green-100 text-green-800">Total Gross</Badge>
                <Minus className="h-4 w-4 text-gray-400" />
                <Badge className="bg-orange-100 text-orange-800">Shipping Cost</Badge>
                <Minus className="h-4 w-4 text-gray-400" />
                <Badge className="bg-red-100 text-red-800">Commission</Badge>
                <Minus className="h-4 w-4 text-gray-400" />
                <Badge className="bg-red-100 text-red-800">Comm. GST</Badge>
                <Minus className="h-4 w-4 text-gray-400" />
                <Badge className="bg-red-100 text-red-800">TDS</Badge>
                <Minus className="h-4 w-4 text-gray-400" />
                <Badge className="bg-purple-100 text-purple-800">TCS</Badge>
                <Minus className="h-4 w-4 text-gray-400" />
                <Badge className="bg-red-100 text-red-800">Gateway</Badge>
                <Minus className="h-4 w-4 text-gray-400" />
                <Badge className="bg-red-100 text-red-800">Gateway GST</Badge>
                <Equal className="h-4 w-4 text-gray-400" />
                <Badge className="bg-blue-600 text-white">Net Payout</Badge>
              </div>
            </div>
            
            {/* Important Note about Commission */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-purple-900">Commission Rate is Tier-Dependent</p>
                  <p className="text-sm text-purple-700 mt-1">
                    The example below uses <strong>15% commission</strong> for illustration purposes only. 
                    Your actual commission rate depends on your <strong>Subscription Plan (Tier)</strong>. 
                    Higher tier plans enjoy lower commission rates.
                  </p>
                  <Button 
                    variant="link" 
                    className="text-purple-600 p-0 h-auto mt-2"
                    onClick={() => navigate('/vendor/subscription-plans')}
                  >
                    View your subscription plan →
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Sample Calculation */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Info className="h-4 w-4 text-blue-600" />
                <p className="text-sm font-medium text-gray-700">
                  Illustrative Example (₹10,000 Product + ₹150 Shipping @ {illustrativeCommissionRate}% Commission)
                </p>
              </div>
              
              <div className="space-y-2">
                {/* Customer Pays Section */}
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Customer Pays</p>
                <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg text-sm">
                  <span className="text-green-700">Product Value</span>
                  <span className="font-medium text-green-600">{formatPrice(sampleGrossSales)}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg text-sm">
                  <span className="text-green-700">Shipping Charge</span>
                  <span className="font-medium text-green-600">{formatPrice(sampleShippingCollected)}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg text-sm">
                  <span className="text-green-700">GST on Shipping (18%)</span>
                  <span className="font-medium text-green-600">{formatPrice(sampleShippingGST.toFixed(2))}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-100 rounded-lg border border-green-200">
                  <span className="text-sm font-semibold text-green-800">Total Gross Collection</span>
                  <span className="font-bold text-green-700">{formatPrice(sampleTotalGross.toFixed(2))}</span>
                </div>
                
                <div className="flex justify-center">
                  <ArrowDown className="h-4 w-4 text-gray-400" />
                </div>
                
                {/* Platform Deductions */}
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Platform Deductions</p>
                
                {/* Shipping/Logistics Cost - First deduction */}
                <div className="flex items-center justify-between p-2 bg-orange-50 rounded-lg text-sm border border-orange-200">
                  <div className="flex flex-col">
                    <span className="text-orange-700">Shipping/Logistics Cost</span>
                    <span className="text-[10px] text-orange-500">Paid to carrier ({formatPrice(sampleShippingCollected)} + {formatPrice(sampleShippingGST.toFixed(0))} GST)</span>
                  </div>
                  <span className="text-orange-600 font-medium">-{formatPrice(sampleShippingCost.toFixed(2))}</span>
                </div>
                
                {/* Net Taxable for TCS */}
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs border border-gray-200">
                  <span className="text-gray-600">Net Taxable (for TCS)</span>
                  <span className="text-gray-700 font-medium">{formatPrice(sampleNetTaxable)}</span>
                </div>
                
                <div className="space-y-1">
                  <div className="flex items-center justify-between p-2 bg-red-50 rounded-lg text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-red-700">Platform Commission ({illustrativeCommissionRate}%)</span>
                      <Badge variant="outline" className="text-[10px] text-purple-600 border-purple-300">Varies by Tier</Badge>
                    </div>
                    <span className="text-red-600 font-medium">-{formatPrice(sampleCommission)}</span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-red-50 rounded-lg text-sm">
                    <span className="text-red-700">GST on Commission (18%)</span>
                    <span className="text-red-600 font-medium">-{formatPrice(sampleCommissionGST.toFixed(2))}</span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-red-50 rounded-lg text-sm">
                    <div className="flex items-center gap-1">
                      <span className="text-red-700">TDS u/s 194-O (0.1%)</span>
                      <span className="text-[10px] text-gray-500">(on net taxable)</span>
                    </div>
                    <span className="text-red-600 font-medium">-{formatPrice(sampleTDS.toFixed(2))}</span>
                  </div>
                  {/* TCS - NEW */}
                  <div className="flex items-center justify-between p-2 bg-purple-50 rounded-lg text-sm border border-purple-200">
                    <div className="flex items-center gap-1">
                      <span className="text-purple-700">TCS under GST (1%)</span>
                      <span className="text-[10px] text-gray-500">(on net taxable)</span>
                    </div>
                    <span className="text-purple-600 font-medium">-{formatPrice(sampleTCS.toFixed(2))}</span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-red-50 rounded-lg text-sm">
                    <span className="text-red-700">Payment Gateway (2%)</span>
                    <span className="text-red-600 font-medium">-{formatPrice(sampleGateway.toFixed(2))}</span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-red-50 rounded-lg text-sm">
                    <span className="text-red-700">GST on Gateway (18%)</span>
                    <span className="text-red-600 font-medium">-{formatPrice(sampleGatewayGST.toFixed(2))}</span>
                  </div>
                </div>
                
                {/* Total Deductions */}
                <div className="flex items-center justify-between p-2 bg-red-100 rounded-lg text-sm font-semibold border border-red-200">
                  <span className="text-red-800">Total Deductions</span>
                  <span className="text-red-700">-{formatPrice(sampleTotalDeductions.toFixed(2))}</span>
                </div>
                
                <div className="flex justify-center">
                  <ArrowDown className="h-4 w-4 text-gray-400" />
                </div>
                
                {/* Net Payout */}
                <div className="flex items-center justify-between p-4 bg-blue-100 rounded-lg border-2 border-blue-300">
                  <div>
                    <span className="text-sm text-blue-900 font-semibold">Your Net Payout</span>
                    <p className="text-xs text-blue-600">
                      Effective deduction: {((sampleTotalDeductions / sampleTotalGross) * 100).toFixed(1)}%
                    </p>
                  </div>
                  <span className="font-bold text-blue-700 text-xl">{formatPrice(sampleNetPayout)}</span>
                </div>
              </div>
            </div>
            
            {/* Deductions Explained */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
              <p className="text-sm font-semibold text-amber-900">
                Deductions Explained:
              </p>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-orange-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-orange-800">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-amber-900">Shipping/Logistics Cost</p>
                    <p className="text-amber-700 text-xs mt-0.5">
                      The actual shipping amount + GST paid to the carrier (Delhivery, BlueDart, etc.). 
                      Deducted first from gross to calculate net taxable.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-amber-800">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-amber-900">Platform Commission</p>
                    <p className="text-amber-700 text-xs mt-0.5">
                      Percentage of Gross Sales for marketplace services. Rate varies based on your subscription tier 
                      (e.g., Basic: 18%, Pro: 15%, Business: 12%, Enterprise: Custom).
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-amber-800">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-amber-900">GST on Commission (18%)</p>
                    <p className="text-amber-700 text-xs mt-0.5">
                      Goods & Services Tax charged on the commission amount as per Indian tax law.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-amber-800">4</span>
                  </div>
                  <div>
                    <p className="font-medium text-amber-900">TDS u/s 194-O (0.1%)</p>
                    <p className="text-amber-700 text-xs mt-0.5">
                      Tax Deducted at Source by e-commerce operators as per Section 194-O of the Income Tax Act. 
                      Calculated on net taxable (after shipping). Adjustable against your income tax liability.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-purple-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-purple-800">5</span>
                  </div>
                  <div>
                    <p className="font-medium text-purple-900">TCS under GST (1%)</p>
                    <p className="text-purple-700 text-xs mt-0.5">
                      Tax Collected at Source under GST law for e-commerce transactions. 
                      Calculated on net taxable (after shipping). Can be claimed as ITC in your GST returns.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-amber-800">6</span>
                  </div>
                  <div>
                    <p className="font-medium text-amber-900">Payment Gateway + GST</p>
                    <p className="text-amber-700 text-xs mt-0.5">
                      Processing fee (2%) for online transactions + 18% GST on gateway fees. 
                      Calculated on total gross collection (including shipping).
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* TDS/TCS Certificate Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-600" />
                <p className="text-sm text-blue-800">
                  <strong>TDS/TCS Certificates:</strong> Form 26AS (TDS) and TCS statements will be available for download quarterly from your dashboard.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-4 md:space-y-6 max-w-6xl mx-auto">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Payouts & Financials</h1>
          <p className="text-sm md:text-base text-gray-500 mt-1">Manage your payout settings and view transaction history</p>
        </div>

        <Tabs defaultValue="settings" className="space-y-4 md:space-y-6">
          <div className="overflow-x-auto">
            <TabsList className="inline-flex w-auto">
              <TabsTrigger value="settings" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                <span className="text-xs md:text-sm">Settings</span>
              </TabsTrigger>
              <TabsTrigger value="upcoming" className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                <span className="text-xs md:text-sm">Upcoming</span>
              </TabsTrigger>
              <TabsTrigger value="history" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span className="text-xs md:text-sm">History</span>
              </TabsTrigger>
              <TabsTrigger value="formula" className="flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                <span className="text-xs md:text-sm">Payout Formula</span>
              </TabsTrigger>
              <TabsTrigger value="estimator" className="flex items-center gap-2" data-testid="tab-estimator">
                <Calculator className="h-4 w-4" />
                <span className="text-xs md:text-sm">Estimator</span>
              </TabsTrigger>
              <TabsTrigger value="wallet" className="flex items-center gap-2" data-testid="tab-wallet">
                <Wallet className="h-4 w-4" />
                <span className="text-xs md:text-sm">Shipping Pool Wallet</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="settings" className="space-y-6">
            {/* Warning if tax registration not complete */}
            {!payoutData.legal_business_name && (
              <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-300 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <AlertCircle className="h-6 w-6 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-bold text-red-900 text-lg mb-2">❌ Tax Registration Required</p>
                    <p className="text-sm text-red-800 mb-4">
                      You cannot configure payout settings without completing tax registration. 
                      Your business and bank details are needed to process payouts.
                    </p>
                    <Button 
                      onClick={() => navigate('/vendor/tax-registration')}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      Complete Tax Registration First →
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSavePayoutSettings} className="space-y-6">
              {/* Info Banner */}
              {payoutData.bank_name && payoutData.legal_business_name && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-900">Bank & tax details auto-populated</p>
                      <p className="text-sm text-blue-700 mt-1">
                        Your bank account and tax information has been automatically filled from your tax registration. 
                        These are verified details - changes may require re-verification.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Bank Account Details</CardTitle>
                      <CardDescription>Where your payouts will be sent</CardDescription>
                    </div>
                    {!isEditingBank ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleStartEdit}
                        className="gap-2"
                        data-testid="edit-bank-details-btn"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Button type="button" variant="ghost" size="sm" onClick={handleCancelEdit}>
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          className="gap-2"
                          onClick={handleRequestOtp}
                          disabled={otpSending}
                          data-testid="verify-save-bank-btn"
                        >
                          {otpSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                          {otpSending ? 'Sending OTP...' : 'Verify & Save'}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isEditingBank && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                      <Lock className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-amber-800">
                        Changing bank details requires OTP verification. A confirmation email will be sent after the update.
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 flex-wrap text-sm">
                        Bank Name *
                        {!isEditingBank && payoutData.bank_name && (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Verified
                          </Badge>
                        )}
                      </Label>
                      <Input
                        data-testid="bank-name-input"
                        value={isEditingBank ? editBankData.bank_name : payoutData.bank_name}
                        onChange={(e) => isEditingBank 
                          ? setEditBankData({ ...editBankData, bank_name: e.target.value })
                          : setPayoutData({ ...payoutData, bank_name: e.target.value })
                        }
                        placeholder="HDFC Bank"
                        readOnly={!isEditingBank}
                        className={!isEditingBank ? 'bg-gray-50 cursor-default' : ''}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Account Number *</Label>
                      <Input
                        data-testid="account-number-input"
                        value={isEditingBank ? editBankData.account_number : payoutData.account_number}
                        onChange={(e) => isEditingBank
                          ? setEditBankData({ ...editBankData, account_number: e.target.value })
                          : setPayoutData({ ...payoutData, account_number: e.target.value })
                        }
                        placeholder="1234567890"
                        readOnly={!isEditingBank}
                        className={!isEditingBank ? 'bg-gray-50 cursor-default' : ''}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 flex-wrap text-sm">
                        IFSC Code *
                        {!isEditingBank && payoutData.ifsc_code && (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Verified
                          </Badge>
                        )}
                      </Label>
                      <Input
                        data-testid="ifsc-code-input"
                        value={isEditingBank ? editBankData.ifsc_code : payoutData.ifsc_code}
                        onChange={(e) => isEditingBank
                          ? setEditBankData({ ...editBankData, ifsc_code: e.target.value.toUpperCase() })
                          : setPayoutData({ ...payoutData, ifsc_code: e.target.value.toUpperCase() })
                        }
                        placeholder="HDFC0001234"
                        readOnly={!isEditingBank}
                        className={!isEditingBank ? 'bg-gray-50 cursor-default' : ''}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Payout Cycle</Label>
                      <Select
                        value={isEditingBank ? editBankData.payout_frequency : payoutData.payout_frequency}
                        onValueChange={(value) => isEditingBank
                          ? setEditBankData({ ...editBankData, payout_frequency: value })
                          : setPayoutData({ ...payoutData, payout_frequency: value })
                        }
                        disabled={!isEditingBank}
                      >
                        <SelectTrigger data-testid="payout-frequency-select" className={!isEditingBank ? 'bg-gray-50' : ''}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="7_days">Every 7 Days</SelectItem>
                          <SelectItem value="15_days">Every 15 Days (Default)</SelectItem>
                          <SelectItem value="30_days">Every 30 Days</SelectItem>
                        </SelectContent>
                      </Select>
                      {(isEditingBank ? editBankData.payout_frequency : payoutData.payout_frequency) === '7_days' && (
                        <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                          <AlertCircle className="h-3 w-3 flex-shrink-0" />
                          7-day cycle is only available when collected amount exceeds &#8377;5,000 in the cycle. Falls back to 15-day otherwise.
                        </p>
                      )}
                    </div>
                    
                    {/* Payout Disclaimer */}
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mt-3">
                      <p className="text-[11px] text-slate-600 leading-relaxed">
                        <strong className="text-slate-700">Important:</strong> Payout cycles are indicative and may be subject to delays due to bank holidays, 
                        payment gateway maintenance, or other third-party service disruptions. Aniyor shall not be held responsible for 
                        delays caused by banking institutions, payment processors (e.g., RazorpayX), or regulatory compliance requirements. 
                        Payouts are processed on the next available business day following the scheduled cycle date.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Business & Tax Information</CardTitle>
                  <CardDescription>Auto-filled from your tax registration</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      Legal Business Name *
                      {payoutData.legal_business_name && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </Label>
                    <Input
                      value={payoutData.legal_business_name}
                      onChange={(e) => setPayoutData({ ...payoutData, legal_business_name: e.target.value })}
                      placeholder="ABC Private Limited"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">PAN Number *</Label>
                      <Input
                        value={payoutData.pan_number}
                        onChange={(e) => setPayoutData({ ...payoutData, pan_number: e.target.value.toUpperCase() })}
                        placeholder="ABCDE1234F"
                        maxLength={10}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">GST Number</Label>
                      <Input
                        value={payoutData.gst_number}
                        onChange={(e) => setPayoutData({ ...payoutData, gst_number: e.target.value.toUpperCase() })}
                        placeholder="22AAAAA0000A1Z5"
                        maxLength={15}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Display Preferences</CardTitle>
                  <CardDescription>What financial info to show on your dashboard</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Total Commission</Label>
                      <p className="text-sm text-gray-500">Display commission breakdown</p>
                    </div>
                    <Switch
                      checked={payoutData.show_total_commission}
                      onCheckedChange={(checked) => setPayoutData({ ...payoutData, show_total_commission: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Balance Due</Label>
                      <p className="text-sm text-gray-500">Display pending payment balance</p>
                    </div>
                    <Switch
                      checked={payoutData.show_balance_due}
                      onCheckedChange={(checked) => setPayoutData({ ...payoutData, show_balance_due: checked })}
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="sticky bottom-0 z-10 bg-white border-t border-gray-200 -mx-4 px-4 py-3 mt-4 flex gap-2 justify-end md:relative md:border-t-0 md:bg-transparent md:mx-0 md:px-0 md:py-0 md:mt-0">
                <Button type="button" variant="outline" onClick={() => window.history.back()}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="upcoming" className="space-y-4 md:space-y-6">
            <Card>
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-xl md:text-2xl">Upcoming Payouts - Next Cycle</CardTitle>
                <CardDescription className="text-sm">Payouts scheduled for the next payment cycle</CardDescription>
              </CardHeader>
              <CardContent className="p-4 md:p-6">
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 md:p-4">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-0">
                      <div>
                        <p className="text-xs md:text-sm text-gray-600">Next Payout Date</p>
                        <p className="text-lg md:text-xl font-bold text-green-700">
                          {upcomingData.next_payout_date ? new Date(upcomingData.next_payout_date).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          }) : 'Not scheduled'}
                        </p>
                      </div>
                      <div className="md:text-right">
                        <p className="text-xs md:text-sm text-gray-600">Expected Amount</p>
                        <p className="text-lg md:text-xl font-bold text-green-700">
                          {formatPrice(upcomingData.expected_amount)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[600px]">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Order ID</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Amount</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Commission</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Net Payout</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {upcomingPayouts.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="px-4 py-8 text-center text-sm text-gray-500">
                                No upcoming payouts scheduled
                              </td>
                            </tr>
                          ) : (
                            upcomingPayouts.map((payout) => (
                              <tr key={payout.id || payout.order_id} className="border-t hover:bg-gray-50 cursor-pointer group"
                                  onClick={() => { setSelectedPayout(payout); setShowSettlementModal(true); }}>
                                <td className="px-4 py-3 text-sm font-medium">#{payout.order_number || payout.order_id?.slice(0,8)}</td>
                                <td className="px-4 py-3 text-sm">{formatPrice(payout.gross_total || payout.amount)}</td>
                                <td className="px-4 py-3 text-sm text-red-600">−{formatPrice(payout.commission)}</td>
                                <td className="px-4 py-3 text-sm font-semibold text-gray-900">{formatPrice(payout.net_payout || payout.net_amount)}</td>
                                <td className="px-4 py-3 text-sm">
                                  <span className="inline-flex items-center gap-1.5">
                                    {getUpcomingBadge(payout.status)}
                                    <span className="text-xs text-blue-600 opacity-0 group-hover:opacity-100 underline underline-offset-2">
                                      Breakdown
                                    </span>
                                  </span>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="space-y-4 md:space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              <Card>
                <CardContent className="pt-4 md:pt-6 pb-3 md:pb-4">
                  <div className="text-center">
                    <p className="text-xs md:text-sm text-gray-600">Pending</p>
                    <p className="text-xl md:text-2xl font-bold">{formatPrice(stats.pending_payouts)}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 md:pt-6 pb-3 md:pb-4">
                  <div className="text-center">
                    <p className="text-xs md:text-sm text-gray-600">Processing</p>
                    <p className="text-xl md:text-2xl font-bold">{formatPrice(stats.processing_payouts)}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 md:pt-6 pb-3 md:pb-4">
                  <div className="text-center">
                    <p className="text-xs md:text-sm text-gray-600">Completed</p>
                    <p className="text-xl md:text-2xl font-bold">{formatPrice(stats.completed_payouts)}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 md:pt-6 pb-3 md:pb-4">
                  <div className="text-center">
                    <p className="text-xs md:text-sm text-gray-600">Total Paid</p>
                    <p className="text-xl md:text-2xl font-bold">{formatPrice(stats.total_paid)}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Payout History</CardTitle>
                <CardDescription>Click any row to view the full 10-line financial breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                {payoutHistory.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <DollarSign className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">No payout history available</p>
                    <p className="text-sm mt-1">Your completed payouts will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="payout-history-list">
                    {payoutHistory.map((payout) => (
                      <div
                        key={payout.id || payout.order_id}
                        className="flex items-center justify-between p-4 border rounded-xl hover:bg-gray-50 cursor-pointer transition-colors group"
                        onClick={() => { setSelectedPayout(payout); setShowSettlementModal(true); }}
                        data-testid={`payout-row-${payout.order_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center shrink-0">
                            <Receipt className="h-4 w-4 text-gray-500" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm text-gray-900">
                                Order #{payout.order_number || payout.order_id?.slice(0, 8)}
                              </p>
                              {payout.has_correction && (
                                <span
                                  className="text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-1.5 py-0.5 whitespace-nowrap"
                                  data-testid={`payout-correction-badge-${payout.order_id}`}
                                >
                                  Legacy payout – corrected receipt available
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {payout.created_at ? new Date(payout.created_at).toLocaleDateString('en-IN', {
                                day: 'numeric', month: 'short', year: 'numeric'
                              }) : ''}
                            </p>
                          </div>
                        </div>

                        {/* Middle: deductions summary */}
                        <div className="hidden sm:flex items-center gap-6 text-xs text-gray-500">
                          <div className="text-center">
                            <p className="text-gray-400">Gross</p>
                            <p className="font-medium text-gray-700">{formatPrice(payout.gross_total)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-gray-400">Commission</p>
                            <p className="font-medium text-red-600">−{formatPrice(payout.commission)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-gray-400">TDS+TCS</p>
                            <p className="font-medium text-red-600">−{formatPrice((payout.tds || 0) + (payout.tcs || 0))}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 shrink-0">
                          <div className="text-right">
                            <p className="text-xs text-gray-400">Net Payout</p>
                            <p className="font-bold text-gray-900" data-testid={`net-${payout.order_id}`}>
                              {formatPrice(payout.net_payout)}
                            </p>
                          </div>
                          {getStatusBadge(payout.status || 'computed')}
                          {(RELEASED_STATUSES.includes((payout.status || '').toLowerCase()) || payout.has_correction) && (
                            <Button
                              variant="ghost" size="sm" className="h-8 px-2"
                              onClick={(e) => { e.stopPropagation(); openPayoutReceipt(payout, true); }}
                              data-testid={`payout-download-receipt-${payout.order_id}`}
                              title="Download Payout Receipt"
                            >
                              <Download className="h-4 w-4 text-gray-500" />
                            </Button>
                          )}
                          <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-gray-600 transition-colors" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="formula">
            <PayoutFormulaContent />
          </TabsContent>

          <TabsContent value="estimator" data-testid="tab-content-estimator">
            <PayoutEstimator />
          </TabsContent>

          <TabsContent value="wallet" data-testid="tab-content-wallet">
            <ShippingPoolWalletPanel />
          </TabsContent>
        </Tabs>
      </div>
      
      {/* OTP Verification Modal for Bank Details Edit */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="otp-verification-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <ShieldCheck className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Verify Your Identity</h3>
                  <p className="text-xs text-gray-500">Enter the OTP sent to ****{user?.mobile?.slice(-4)}</p>
                </div>
              </div>
              <button onClick={() => { setShowOtpModal(false); setOtpValue(''); }} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Enter OTP</Label>
              <Input
                data-testid="otp-input"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otpValue}
                onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, ''))}
                placeholder="Enter 6-digit OTP"
                className="text-center text-lg tracking-widest font-mono"
                autoFocus
              />
            </div>

            <div className="bg-gray-50 rounded-lg p-3 space-y-1">
              <p className="text-xs font-medium text-gray-700">Changes to be saved:</p>
              <div className="text-xs text-gray-600 space-y-0.5">
                <p>Bank: <strong>{editBankData.bank_name}</strong></p>
                <p>Account: <strong>****{editBankData.account_number?.slice(-4)}</strong></p>
                <p>IFSC: <strong>{editBankData.ifsc_code}</strong></p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowOtpModal(false); setOtpValue(''); }}
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-otp-btn"
                className="flex-1 gap-2"
                onClick={handleVerifyAndSave}
                disabled={otpVerifying || otpValue.length < 4}
              >
                {otpVerifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {otpVerifying ? 'Verifying...' : 'Confirm & Save'}
              </Button>
            </div>

            <button
              onClick={handleRequestOtp}
              disabled={otpSending}
              className="w-full text-center text-sm text-blue-600 hover:underline disabled:opacity-50"
            >
              {otpSending ? 'Sending...' : 'Resend OTP'}
            </button>
          </div>
        </div>
      )}

      {/* Full SSOT Payout Breakdown Modal */}
      {showSettlementModal && selectedPayout && (
        <PayoutBreakdownModal
          payout={selectedPayout}
          onClose={() => { setShowSettlementModal(false); setSelectedPayout(null); }}
        />
      )}
    </DashboardLayout>
  );
};

export default VendorPayouts;
